import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { verifyOtp } from "../api/auth";
import "./auth.css";

const getErrorMessage = (err: any): string => {
  if (!err) return "Invalid or expired code.";

  if (typeof err === "string") return err;

  if (Array.isArray(err)) {
    return err.map((e) => e.msg || "Invalid code").join(", ");
  }

  if (typeof err === "object" && err.msg) {
    return err.msg;
  }

  return "Invalid or expired code.";
};


interface Props {
  email: string;
  onSuccess(): void;
}

const API = "https://api.gelwelpartners.com"; // or localhost

export default function VerifyOtp({ email, onSuccess }: Props) {
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(60);
  const [resending, setResending] = useState(false);

  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  /* Auto focus first input */
  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  /* Countdown timer */
  useEffect(() => {
    if (seconds <= 0) return;

    const t = setInterval(() => {
      setSeconds((s) => s - 1);
    }, 1000);

    return () => clearInterval(t);
  }, [seconds]);

  const handleChange = (i: number, val: string) => {
    if (!/^\d?$/.test(val)) return;

    const next = [...code];
    next[i] = val;
    setCode(next);

    if (val && inputs.current[i + 1]) {
      inputs.current[i + 1]?.focus();
    }
  };

  const submit = async () => {
    setError(null);
    const otp = code.join("");

    if (otp.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }

    try {
      setLoading(true);
      const res = await verifyOtp({ email, code: otp });
      localStorage.setItem("token", res.data.access_token);
      onSuccess();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(getErrorMessage(detail));
    }
    finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      setResending(true);
      await axios.post(`${API}/resend-otp`, { email });
      setSeconds(60);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(getErrorMessage(detail));
    }
    finally {
      setResending(false);
    }
  };

  return (
    <motion.div
      className="auth-page animated-bg"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="otp-shell"
        initial={{ scale: 0.95, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <h2>Verify your email</h2>
        <p>Enter the 6-digit code sent to {email}</p>

        <div className="otp-inputs">
          {code.map((v, i) => (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              value={v}
              onChange={(e) => handleChange(i, e.target.value)}
              maxLength={1}
            />
          ))}
        </div>

        {error && <div className="auth-error">{error}</div>}

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={submit}
          disabled={loading}
        >
          {loading ? "Verifying…" : "Verify & continue"}
        </motion.button>

        {/* RESEND SECTION */}
        <div className="resend-wrapper">
          {seconds > 0 ? (
            <span className="resend-timer">
              Resend code in <strong>{seconds}s</strong>
            </span>
          ) : (
            <motion.span
              className="resend-link"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resend}
            >
              {resending ? "Sending…" : "Resend code"}
            </motion.span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
